import { Controller } from "@hotwired/stimulus"
import { SLACK_OAUTH_STORAGE_PREFIX } from "../lib/slack_oauth_transport"

export default class extends Controller {
  static targets = [ "frame" ]
  static values = {
    url: String,
    flow: String,
    channel: { type: String, default: "slack_oauth" },
    width: { type: Number, default: 600 },
    height: { type: Number, default: 700 }
  }

  connect() {
    this.popup = null
    this.pollTimer = null
    this.boundWindowMessage = this.handleWindowMessage.bind(this)
    window.addEventListener("message", this.boundWindowMessage)

    try {
      this.channel = new BroadcastChannel(this.channelValue)
      this.channel.onmessage = (event) => this.handlePayload(event.data)
    } catch {
      this.channel = null
    }
  }

  disconnect() {
    this.channel?.close()
    window.removeEventListener("message", this.boundWindowMessage)
    this.closePopup()
  }

  open(event) {
    event.preventDefault()

    const left = window.screenX + (window.outerWidth - this.widthValue) / 2
    const top = window.screenY + (window.outerHeight - this.heightValue) / 2
    const oauthUrl = new URL(this.urlValue, window.location.origin)
    oauthUrl.searchParams.set("flow", this.flowValue)

    this.popup = window.open(
      oauthUrl.toString(),
      "oauth_popup",
      `width=${this.widthValue},height=${this.heightValue},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
    )

    if (!this.popup) return

    this.pollTimer = setInterval(() => this.checkFallback(), 350)
  }

  handleWindowMessage(event) {
    if (event.source !== this.popup) return
    if (event.origin !== window.location.origin) return
    this.handlePayload(event.data)
  }

  handlePayload(data) {
    if (data?.type !== "oauth_complete") return

    const expectedFlow = String(this.flowValue || "")
    const incomingFlow = String(data.flowToken || "")
    if (incomingFlow !== expectedFlow) return

    this.closePopup()
    if (!data.success || !this.hasFrameTarget || !data.oauthData) return

    this.renderConnectedState(data.oauthData)
  }

  checkFallback() {
    if (this.popup?.closed) {
      this.closePopup()
      return
    }

    const key = SLACK_OAUTH_STORAGE_PREFIX + String(this.flowValue || "")
    let raw
    try {
      raw = localStorage.getItem(key)
    } catch {
      return
    }

    if (!raw) return

    try {
      const data = JSON.parse(raw)
      localStorage.removeItem(key)
      this.handlePayload(data)
    } catch {
      /* ignore */
    }
  }

  closePopup() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }

    if (this.popup) {
      this.popup.close()
      this.popup = null
    }
  }

  renderConnectedState(oauthData) {
    const escapedChannel = this.escapeHtml(oauthData.slack_channel || "")
    const escapedUrl = this.escapeHtml(oauthData.url || "")

    this.frameTarget.innerHTML = `
      <input type="hidden" name="integration[url]" id="integration_url" value="${escapedUrl}">
      <div>
        <p class="mb-1 block text-sm font-medium text-fg/80">Channel</p>
        <p class="text-base font-medium text-fg">${escapedChannel}</p>
      </div>
    `
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll("\"", "&quot;")
      .replaceAll("'", "&#39;")
  }
}
